import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Button, Chip, Tooltip, Combobox as Combo, useCombobox, CheckIcon, CloseButton, Menu, Indicator, ScrollArea, ComboboxStore } from '@mantine/core';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { StyleProps } from 'components';
import { usePortal } from 'components/portal/portal';
import { DateTime } from 'luxon';
import { useState, useEffect, useMemo, ReactNode, createContext, Fragment, useRef, JSX } from 'react';
import { MdHome, MdUnfoldLessDouble, MdUnfoldMore, MdUnfoldMoreDouble } from 'react-icons/md';
import { CommentItem, commentStatusSummary, sortOptions, UseCommentPost, useComments, UseComments, useCommentsQuery, zCommentQuery, type CommentQuery } from './comments';
import { CommentRow, zCommentCfg } from './commentTypes';
import { CommentStatuses } from './CommentStatus';
import { Compose } from './Compose';
import { tree, splitBy, concat, filterObj, mapWithPrev } from 'shared/collection';
import { NotNull } from 'shared/type';
import { Paths, pathValue } from 'shared/paths';
import { delay, doSafe, sansOk } from 'shared/promise';
import { fmtDate } from 'shared/format';
import { QuestionCfg, SubqCfg, Subqs } from 'shared/forecast';
import { Markdown, WrapIf, SErrorBoundary, MarkdownUtils as Md, useMemoValue } from 'shared/ui';
import { HiArrowSmDown, HiCalendar, HiChevronDoubleDown, HiDotsVertical, HiLightBulb, HiSelector, HiServer, HiViewList } from 'react-icons/hi';
import { SAvatar } from 'components/portal/SAvatar';
import { CardCollapsable } from 'components/Card';
import { sortBy, pick, mapToObj, isEmpty, indexBy, mapValues, isArray, pipe, entries } from 'remeda';
import { useStickyOrder } from 'components/hooks/useStickyOrder';
import { IconContext } from 'react-icons';
import { useQueryString } from 'utils/useQueryString';
import { z } from 'zod';
import { match, P } from 'ts-pattern';
import { useStickyBottomScroll } from 'components/hooks';
import { MentionQuestionBadge } from 'components/form/lexical/MentionNode';
import { HiChatBubbleLeft, HiMagnifyingGlass } from 'react-icons/hi2';
import { Comment, CommentProps, CommentSkeleton, ShowMoreCommentsTitle } from './Comment';
import { isApproved, subqStatusMd, useSubqQuery } from 'components/question/subq';
import Fuse from 'fuse.js';
import rehypeRaw from 'rehype-raw';
import { markMd } from 'components/form/MarkText';
import { SubqTypeIcon } from 'components/question/Subqs';
import { QuestionStatusIcon } from 'components/question/QuestionStatus';
import { VoteButtons } from 'components/question/VoteButtons';
import { reactionPropsForVote } from 'components/question/Subqs';
import { useQuestionContext } from 'components/question/questionComponentTypes';
import { useQuestionQuery, useQuestionUIItem } from 'components/question/useQuestions';
import { useLogOnValueChange } from 'components/hooks/useLogValue';
import { ReactionSummaryOfType, useQuestionVotes, useVote } from 'components/question/reactions';
export const isNewComment = (latestView: DateTime | undefined | null, m: number | string) => match(latestView).with(undefined, () => undefined).with(null, () => true).otherwise(l => match(m).with(P.string, m => DateTime.fromISO(m).toMillis()).with(P.number, m => m).exhaustive() > l.toMillis());
const getRoot = <T extends {
  id: string;
  parent_id?: string;
},>(id: string, byId: Record<string, T>) => {
  while (true) {
    const c = byId[id];
    if (!c) return undefined;
    if (!c.parent_id) return c;
    id = c.parent_id;
  }
};
export const useCommentStats = t0 => {
  const $ = _c(16);
  const {
    question_id,
    subquestion_id
  } = t0;
  const ctx = useQuestionContext();
  let t1;
  if ($[0] !== ctx) {
    t1 = ctx ?? {};
    $[0] = ctx;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    latestView
  } = t1;
  let t2;
  if ($[2] !== question_id || $[3] !== subquestion_id) {
    t2 = {
      question_id,
      subquestion_id
    };
    $[2] = question_id;
    $[3] = subquestion_id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = useCommentsQuery(t2);
  const {
    data: comments
  } = query;
  let t3;
  if ($[5] !== comments || $[6] !== latestView) {
    t3 = comments?.filter(c => isNewComment(latestView, c.created_at)).length;
    $[5] = comments;
    $[6] = latestView;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = comments?.length;
  let t5;
  if ($[8] !== comments) {
    t5 = comments ?? [];
    $[8] = comments;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = commentStatusSummary(t5);
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t6) {
    t7 = {
      unread: t3,
      total: t4,
      statusSummary: t6
    };
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export const useCommentPanel = ({
  disabled
}: {
  disabled?: boolean;
}) => {
  const ctx = useQuestionContext();
  const {
    latestView,
    question_id
  } = ctx ?? {};
  const {
    query: rawQuery,
    setQuery
  } = useQueryString(zCommentQuery);
  const query = rawQuery ?? zCommentQuery.parse({});
  const question = doSafe(useQuestionQuery({
    question_id,
    pick: q => pick(q.cfg, ['title', 'subquestionOrder', 'disableProposed'])
  }), q_0 => q_0.data?.ok ? {
    id: question_id!,
    ...sansOk(q_0.data)
  } : undefined);
  const {
    comment_id: commentId,
    subquestion: selectedSubq,
    comment_hide_forecasts: hideForecastComments
  } = query;
  const uComments = useComments({
    question_id,
    disabled
  });
  const {
    comments,
    post,
    deleteComment
  } = uComments;
  const votes = useQuestionVotes({
    question_id,
    disabled
  });
  const [displayMode, setDisplayMode] = useLocalStorage<DisplayMode>({
    key: 'comment-display-mode',
    defaultValue: 'grouped'
  });
  const {
    data: subqsRaw,
    isLoading: subqsLoading
  } = useSubqQuery({
    question_id,
    disabled,
    pick: ({
      id,
      cfg,
      created_by
    }) => ({
      id,
      cfg: pick(cfg, ['title', 'subquestionOrder', 'status', 'subquestionType', 'childrenEnabled']),
      created_by
    })
  });
  const subqs = subqsRaw && question ? Subqs.group(subqsRaw, {
    question,
    showForecasts: 'all'
  }).flatMap(g => g.subqs.map(s => ({
    ...s,
    groupCfg: g.cfg
  }))) : undefined;
  const groupIds = displayMode == 'flat' ? [selectedSubq ?? '_'] : ctx?.subquestion_id ? [ctx.subquestion_id] : concat('_', subqsRaw?.map(s_0 => s_0.id));
  const rootCommentsRaw = !comments ? undefined : pipe(comments.roots.filter(r => displayMode == 'flat' ? selectedSubq == null || selectedSubq == '_' && r.subquestion_id == null || r.subquestion_id == selectedSubq : true),
  // sorting notes: they come in most recent at bottom all the way down the tree and this is used by chart, 'group' however:
  // 1. we order groups (further down) as they are shown in the app (regardless of comments)
  // 2. we order root in groups based on the sort: either recent at top, or insightful at top
  rs => displayMode == 'flat' ? rs : sortBy(rs, [r_0 => query.sort == 'insightful' ? r_0.reactionAgg?.insightful?.count ?? 0 : 0, 'desc'], [r_1 => DateTime.fromISO(r_1.updated_at).toUnixInteger(), 'desc']));
  const {
    data: rootComments,
    reset: resetSort
  } = useStickyOrder(rootCommentsRaw); // new comments from others should just go to the bottom when we are allready here
  useEffect(() => resetSort(), [query.sort, displayMode]);
  type GroupState = {
    opened?: boolean;
  };
  const [groupState, setGroupState] = useState<{
    [id: string]: GroupState;
  }>({});
  const commentGroupsRaw = (() => {
    if (!question) return undefined;
    const emptyGroups = groupIds.map((id_0, sortIndex) => {
      const subquestion = id_0 == '_' ? undefined : doSafe(subqs?.find(s_1 => s_1.id == id_0), ({
        created_by: created_by_0,
        cfg: cfg_0
      }) => ({
        ...cfg_0,
        created_by: created_by_0
      }));
      const approved = subquestion && isApproved(subquestion);
      return {
        id: id_0,
        type: displayMode == 'flat' ? 'flat' : id_0 == '_' ? 'question' : 'subquestion',
        subquestion_id: id_0 == '_' ? undefined : id_0,
        subquestion,
        sortIndex,
        approved,
        hidden: displayMode == 'grouped' ? question.disableProposed && subquestion && !approved : false
      } as CommentGroup;
    });
    if (!rootComments) return emptyGroups;
    const isNew = (m: number | string) => isNewComment(latestView, m);
    const groups = emptyGroups.map(g_0 => {
      const {
        id: id_1
      } = g_0;
      const groupRoots = (displayMode == 'flat' ? rootComments : rootComments.filter(r_2 => id_1 == '_' ? r_2.subquestion_id == null : r_2.subquestions.includes(id_1) || r_2.subquestion_id == id_1)).map((r_3, i) => ({
        ...r_3,
        isOld: !isNew(r_3.created_at) && i >= 20 && (r_3.cfg.status?.length ?? 0) == 0,
        isHidden: hideForecastComments ? r_3.cfg.type == 'forecast-note' : false
      }));
      const comments_0 = tree(groupRoots).treeMap(({
        children,
        ...c
      }) => ({
        ...c,
        highlight: c.id == commentId,
        isNew: isNew(c.created_at)
      }) as Omit<CommentItemChild, 'children'>) as any as CommentItemRoot[];
      const commentsById = indexBy(tree(comments_0).all(), c_0 => c_0.id);
      const voteData = g_0.type == 'subquestion' && g_0.subquestion_id ? votes.getVote(g_0.subquestion_id) : undefined;
      return {
        ...g_0,
        comments: comments_0,
        commentsById,
        voteData
      } satisfies CommentGroup;
    });
    return sortBy(groups, [g_1 => g_1.type == 'question' ? 0 : g_1.approved ? 1 : 2, 'asc'], [g_2 => g_2.type == 'question' || g_2.approved ? g_2.sortIndex : -(g_2.voteData?.vote_total ?? 0), 'asc']);
  })();
  const allGroupState = () => ({
    ...mapToObj(commentGroupsRaw ?? [], g_3 => [g_3.id, {}])
  }); // also returns groups without state

  // on filter, close the others commentSubquestion
  useEffect(() => {
    const sq = selectedSubq;
    if (!sq) return;
    setGroupState({
      // close others and open this because the state might not include it and needs to be added.
      // we make this undefined and rely on default behavior of expand
      ...mapValues(filterObj(groupState, k => k != sq), g_4 => ({
        ...g_4,
        opened: undefined
      })),
      [sq]: {
        ...groupState[sq],
        opened: true
      }
    });
  }, [selectedSubq]);

  // sync the group state with expand comments
  useEffect(() => {
    setGroupState(mapValues(allGroupState(), g_5 => ({
      ...g_5,
      opened: query.comments_expand
    })));
  }, [query.comments_expand]);

  // sync the group state and other filters with comment highlight
  useEffect(() => {
    if (!commentId) return;
    const group = displayMode == 'flat' ? commentGroupsRaw?.[0] : commentGroupsRaw?.find(g_6 => g_6.commentsById?.[commentId]);
    let queryUpdate: Partial<CommentQuery> = {};
    if (group) {
      const groupId = group.id;
      if (displayMode == 'grouped') setGroupState({
        ...groupState,
        [groupId]: {
          ...groupState[groupId],
          opened: true
        }
      });
      const comment = group.commentsById?.[commentId];
      if (comment == null || query.subquestion && query.subquestion != groupId) queryUpdate.subquestion = undefined;
      const byId = group.commentsById;
      const root = comment && byId ? getRoot(comment.id, byId) : undefined;
      if (query.comment_hide_forecasts && root?.cfg.type == 'forecast-note') queryUpdate.comment_hide_forecasts = undefined;
    } else {
      // if we cn't the group at all (.e.g we are in flat mode and filtered out the comments group)
      queryUpdate.subquestion = undefined;
    }
    if (!isEmpty(queryUpdate)) setQuery(queryUpdate);
    delay(2000).then(() => setQuery({
      comment_id: undefined
    }));
  }, [commentId]);
  const commentGroupsWithState = useMemo(() => commentGroupsRaw?.map(g_7 => {
    const openedState = groupState?.[g_7.id]?.opened;
    const highlighted = g_7.id == selectedSubq;
    return {
      id: g_7.id,
      group: g_7,
      opened: openedState ?? (selectedSubq ? highlighted : true),
      highlighted: highlighted,
      setOpened: (opened: boolean) => setGroupState({
        ...groupState,
        [g_7.id]: {
          opened
        }
      })
    };
  }), [commentGroupsRaw, groupState]);
  const {
    data: commentGroups
  } = useStickyOrder(commentGroupsWithState, {
    disabled: latestView === undefined || comments == null
  });
  return {
    commentGroups,
    question,
    subqs,
    latestView,
    setGroupState,
    groupIds,
    displayMode,
    setDisplayMode,
    query,
    setQuery,
    resetSort,
    post,
    deleteComment,
    isLoading: uComments.isLoading || subqsLoading
  };
};
type UseCommentPanel = ReturnType<typeof useCommentPanel>;
export type CommentPanelProps = {
  menuLeft?: ReactNode;
  collapsed?: boolean;
};
type DisplayMode = 'grouped' | 'flat';
export const CommentPanel = ({
  menuLeft,
  collapsed
}: CommentPanelProps) => {
  const uPanel = useCommentPanel({});
  const {
    question,
    subqs,
    commentGroups,
    latestView,
    displayMode,
    query,
    setQuery,
    resetSort,
    post: postRaw,
    deleteComment,
    isLoading
  } = uPanel;
  const subquestionById = useMemo(() => mapToObj(subqs ?? [], s => [s.id, pick(s.cfg, ['title'])]), [subqs]);
  const post = async (c: UseCommentPost) => await postRaw(c).then(() => resetSort()); // // new comments from ourselves means we need to reset the sorting rather than tack it on
  const groupProps = useMemo(() => commentGroups?.filter(g => !g.group.hidden).map(g_0 => ({
    ...g_0,
    key: g_0.id,
    latestView,
    subquestions: subquestionById,
    displayMode,
    query,
    setQuery
  })), [commentGroups, subquestionById, query]);
  const {
    ref: scrollRef,
    scrollToBottom,
    scrollingManual
  } = useStickyBottomScroll();
  const {
    subquestion: selectedSubq
  } = query;
  useEffect(() => {
    if (displayMode != 'flat') return;
    // not sure why but there was a race condition and it didn't always scroll without his delay
    delay(50).then(() => scrollToBottom());
  }, [displayMode, selectedSubq]);
  return <div className={`h-full grid ${displayMode == 'flat' ? 'grid-rows-[auto,1fr,auto]' : 'grid-rows-[auto,1fr]'} `}>
      <TopMenu {...{
      menuLeft,
      collapsed,
      ...pick(uPanel, ['displayMode', 'setDisplayMode', 'query', 'setQuery', 'subqs'])
    }} />
      {!isLoading && question && groupProps ? match(displayMode).with('flat', () => {
      const g_1 = groupProps[0];
      return !g_1 ? <></> : <>
                  <ScrollArea viewportRef={scrollRef} className='py-1 md:pr-3 pr-1 relative'>
                    <CommentGroupPanel {...g_1} key={g_1.key} displayMode='flat' question={question} post={post} deleteComment={deleteComment} />
                    {scrollingManual && <div className='sticky bottom-1 flex justify-center'>
                        <Button size='sm' onClick={() => scrollToBottom()} className='!bg-white !rounded-full shadow-md ' leftSection={<HiChevronDoubleDown />}>
                          latest
                        </Button>
                      </div>}
                  </ScrollArea>
                  <div className='border-t-2 pr-1 pb-1'>
                    <RootCompose post={post} selectedSubq={selectedSubq} />
                  </div>
                </>;
    }).with('grouped', () => <div className='flex flex-col gap-2 pr-1 overflow-y-auto relative'>
              {groupProps?.map(g_2 => <CommentGroupPanel {...g_2} key={g_2.key} question={question} post={post} deleteComment={deleteComment} displayMode='grouped' />)}
            </div>).otherwise(() => <></>) : <CommentSkeleton className='mx-4 mt-4' />}
    </div>;
};
const RootCompose = t0 => {
  const $ = _c(7);
  const {
    post,
    selectedSubq
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      schema: zCommentCfg,
      subPath: ["_"],
      type: "new-group-comment"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const rootComment = useQuestionUIItem(t1);
  let t2;
  if ($[1] !== post || $[2] !== selectedSubq) {
    t2 = cfg => post({
      cfg,
      subquestion_id: selectedSubq
    });
    $[1] = post;
    $[2] = selectedSubq;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== rootComment || $[5] !== t2) {
    t3 = <Compose onPost={t2} readonly={false} hideBorder={true} placeholder="write a new comment..." className="py-2 " showStatus={true} ui={rootComment} autoFocus={true} />;
    $[4] = rootComment;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export type CommentItemChild = CommentItem<{
  highlight: boolean;
  isNew: boolean;
  isOld: boolean;
}>;
type CommentItemRoot = CommentItemChild & {
  isCollapsed: boolean;
  isHidden: boolean;
};
type CommentGroup = {
  id: string;
  type: 'flat' | 'question' | 'subquestion';
  subquestion_id: string | undefined;
  subquestion?: SubqCfg & {
    created_by: string;
  };
  comments?: CommentItemRoot[];
  commentsById?: Record<string, CommentItemChild>;
  sortIndex: number;
  approved?: boolean;
  hidden?: boolean;
  voteData?: ReactionSummaryOfType<'vote'>;
};
type CommentGroupUI = NotNull<UseCommentPanel['commentGroups']>[number];
export type GroupPanelContext = {
  displayMode: DisplayMode;
  subquestions: Record<string, {
    title: string;
  }>;
  query: z.infer<typeof zCommentQuery>;
};
export const GroupPanelContext = createContext<GroupPanelContext>({
  displayMode: 'flat',
  subquestions: {},
  query: zCommentQuery.parse({})
});
export type CommentGroupPanelProps = Pick<UseComments, 'deleteComment' | 'post'> & {
  latestView?: DateTime | null;
  question: {
    id: string;
  } & Pick<QuestionCfg, 'title'>;
} & GroupPanelContext & CommentGroupUI & StyleProps;
export const CommentGroupPanel = ({
  group,
  highlighted,
  className,
  style,
  subquestions,
  ...props
}: CommentGroupPanelProps) => {
  const {
    question,
    post: postRaw,
    deleteComment,
    opened: groupOpened,
    setOpened: setGroupOpened,
    displayMode,
    query
  } = props;
  const {
    comments: commentsRaw,
    subquestion_id,
    subquestion,
    type,
    commentsById
  } = group;
  const comments = useMemo(() => commentsRaw?.filter(c => !c.isHidden), [commentsRaw]);
  const {
    user,
    profile
  } = usePortal();
  const statusSummary = comments ? commentStatusSummary(comments) : undefined;
  const post = (c_0: UseCommentPost) => postRaw({
    ...c_0,
    subquestion_id
  });
  const groupCtx = useMemo(() => ({
    displayMode,
    subquestions,
    query
  }), [displayMode, subquestions, query]);
  const {
    comment_id: highlightId
  } = query;
  const [oldOpened, setOldOpened] = useState<boolean>(false);
  const noOldComments = type == 'flat' || query.subquestion != null && query.subquestion == subquestion_id;
  useEffect(() => {
    if (!highlightId || !commentsById) return;
    const c_1 = commentsById[highlightId];
    if (!c_1) return;
    if (getRoot(c_1?.id, commentsById)?.isOld) setOldOpened(true);
  }, [highlightId]);

  // sync the expand with the show-older-comments section
  useEffect(() => {
    setOldOpened(query.comments_expand ? true : false);
  }, [query.comments_expand]);
  const [csShown, csCollapsed] = useMemo(() => comments && profile && question ? splitBy(comments.map(c_2 => ({
    editable: profile.id == c_2.created_by,
    comment: c_2,
    onPost: post,
    deleteComment,
    displayMode
  })), c_3 => noOldComments || !c_3.comment.isOld) : [], [comments, profile, question, displayMode, query]);
  const titleRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (query.subquestion == null || query.subquestion != subquestion_id || !groupOpened) return;
    delay(10).then(() => {
      titleRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }, [query.subquestion]);
  const ui = useQuestionUIItem({
    schema: zCommentCfg,
    subPath: [group.id],
    type: 'new-group-comment'
  });
  const voteCrud = useVote({
    question_id: question!.id,
    subquestion_id
  });
  if (!user || !profile || !question) return <></>;
  return <GroupPanelContext.Provider value={groupCtx}>
      {type == 'flat' ? <CommentsComponent comments={csShown} className={className} /> : <CardCollapsable opened={groupOpened} onChange={setGroupOpened} classNames={{
      container: `my-1 ${className ?? ''}`,
      title: `${highlighted ? 'bg-[color-mix(in_oklch,var(--mantine-color-primary-filled),var(--mantine-color-body)_90%)]' : ''}`
    }} disableAnimation sticky title={() => <div ref={titleRef} id={`title-${subquestion_id}`} className={`flex items-center gap-2`}>
              <div className='flex-grow min-w-0'>
                {match(type).with('question', () => <div className='flex items-center gap-2'>
                      <MdHome className='shrink-0' /> {question?.title ?? 'QUESTION'}
                    </div>).with('subquestion', () => doSafe(subquestion, sq => <div className='flex items-center gap-1'>
                        <SubqTypeIcon type={sq.subquestionType} /> {sq.title}
                      </div>)).otherwise(() => undefined)}
              </div>
              {statusSummary && <CommentStatuses statusSummary={statusSummary} />}
              <div className='ml-auto flex gap-2 flex-shrink-0 items-center'>
                {doSafe(group.subquestion?.status, s => s != 'approved' ? <QuestionStatusIcon size='sm' item={{
          md: subqStatusMd[s],
          value: s
        }} /> : undefined)}
                {group.subquestion?.status == 'proposed' && <VoteButtons {...reactionPropsForVote({
          vote: voteCrud.vote,
          save: voteCrud.save,
          profile,
          subq: {
            id: group.subquestion_id!,
            created_by: group.subquestion?.created_by ?? '',
            cfg: {
              status: group.subquestion?.status ?? 'proposed'
            }
          }
        })} />}
                {group.commentsById && comments && <CommentsStats total={tree(comments).all().length} unread={tree(comments).all().filter(c_4 => c_4.isNew).length} variant='default' />}
              </div>
            </div>}>
          <div className='flex flex-col gap-2 pl-0'>
            <Compose onPost={cfg => post({
          cfg
        })} readonly={false} placeholder='write a new comment...' className='mb-1 border-b border-gray-300/20' showStatus hideBorder ui={ui} />
            <CommentsComponent comments={csShown} />
            {csCollapsed && csCollapsed.length > 0 && !noOldComments && <CardCollapsable opened={oldOpened} onChange={setOldOpened} variant='text' classNames={{
          title: 'ml-2'
        }} title={o => o ? <p className='pt-2 text-sm'>show less comments</p> : <ShowMoreCommentsTitle comments={csCollapsed.map(c_5 => c_5.comment)} />}>
                <CommentsComponent comments={csCollapsed} />
              </CardCollapsable>}
          </div>
        </CardCollapsable>}
    </GroupPanelContext.Provider>;
};
type MenuComponentProps = {
  inPopup: boolean;
  displayMode: DisplayMode;
  setDisplayMode: (mode: DisplayMode) => void;
  query: CommentQuery;
  setQuery: (query: Partial<CommentQuery>) => void;
  searchStore: UseSearchComments;
  setMenuOpen: (open: boolean) => void;
  subqs: UseCommentPanel['subqs'];
  sqCombo: ComboboxStore;
};
const menuItems = {
  display: {
    section: 'menu',
    El: ({
      inPopup,
      setDisplayMode,
      displayMode
    }: MenuComponentProps) => inPopup ? <Menu.Item onClick={() => setDisplayMode(displayMode == 'grouped' ? 'flat' : 'grouped')} leftSection={displayMode == 'grouped' ? <HiViewList /> : <HiServer />}>
          {displayMode == 'grouped' ? 'display - chat' : 'display - group by question'}
        </Menu.Item> : <Tooltip position='top-end' label={<div>
              Displaying comments <b>{displayMode == 'grouped' ? 'grouped by question' : 'as a "chat"'}.</b> Click to
              toggle
            </div>}>
          <ActionIcon onClick={() => setDisplayMode(displayMode == 'grouped' ? 'flat' : 'grouped')}>
            {displayMode == 'grouped' ? <HiServer /> : <HiViewList />}
          </ActionIcon>
        </Tooltip>
  },
  expand: {
    section: 'menu',
    El: ({
      inPopup,
      query: {
        comments_expand
      },
      setQuery
    }: MenuComponentProps) => {
      const next = match(comments_expand).with(P.nullish, () => true).with(true, () => false).with(false, () => undefined).exhaustive();
      const {
        label,
        Icon
      } = match(next).with(true, () => ({
        label: 'expand all groups and comments',
        Icon: MdUnfoldMoreDouble
      })).with(false, () => ({
        label: 'collapse all groups and comments',
        Icon: MdUnfoldLessDouble
      })).with(P.nullish, () => ({
        label: 'default display of groups and comments',
        Icon: MdUnfoldMore
      })).exhaustive();
      const onClick = () => setQuery({
        comments_expand: next
      });
      return inPopup ? <Menu.Item onClick={onClick} leftSection={<Icon />}>
            {label}
          </Menu.Item> : <Tooltip position='top-end' label={label}>
            <ActionIcon onClick={onClick}>
              <Icon />
            </ActionIcon>
          </Tooltip>;
    }
  },
  search: {
    section: 'menu',
    El: ({
      inPopup,
      setQuery,
      searchStore
    }: MenuComponentProps) => inPopup ? <Menu.Item onClick={() => searchStore.combo.openDropdown()} leftSection={<HiMagnifyingGlass />}>
          search
        </Menu.Item> : <SearchComments {...{
      store: searchStore,
      onCommentSelected: id => {
        setQuery({
          comment_id: id
        });
      }
    } satisfies SearchCommentProps} />
  },
  sort: {
    section: 'filter',
    El: ({
      inPopup,
      query,
      setQuery,
      displayMode
    }: MenuComponentProps) => displayMode != 'grouped' ? undefined : inPopup ? <div className='flex gap-2 wrap'>
          {sortOptions.map(s => <Button size='compact-sm' className={s == query.sort ? undefined : 'opacity-50'} variant={s == query.sort ? 'light' : 'subtle'} leftSection={match(s).with('recent', () => <>
                    <HiArrowSmDown />
                    <HiCalendar />
                  </>).with('insightful', () => <>
                    <HiArrowSmDown />
                    <HiLightBulb />
                  </>).exhaustive()} onClick={() => setQuery({
        sort: s
      })}>
              {s}
            </Button>)}
        </div> : <Tooltip position='top-end' label={<div>
              Comments currently ordered by <b>{query.sort}</b> at top. Click to toggle
            </div>}>
          <div>
            <Button className={'opacity-80 !px-1'} size='compact-sm' variant='subtle' onClick={() => setQuery({
          sort: query.sort == 'insightful' ? 'recent' : 'insightful'
        })}>
              {match(query.sort).with('recent', () => <>
                    <HiArrowSmDown />
                    <HiCalendar />
                  </>).with('insightful', () => <>
                    <HiArrowSmDown />
                    <HiLightBulb />
                  </>).exhaustive()}
            </Button>
          </div>
        </Tooltip>
  },
  hide_forecast: {
    section: 'filter',
    El: ({
      inPopup,
      query: {
        comment_hide_forecasts
      },
      setQuery,
      setMenuOpen
    }: MenuComponentProps) => inPopup || comment_hide_forecasts ? <Chip checked={comment_hide_forecasts} onChange={() => {
      setQuery({
        comment_hide_forecasts: !comment_hide_forecasts
      });
      setMenuOpen(false);
    }}>
          {inPopup ? 'hide forecast comments' : 'hide forecasts'}
        </Chip> : undefined
  },
  subquestion: {
    section: 'filter',
    El: ({
      inPopup,
      displayMode,
      query: {
        subquestion: selectedSubq
      },
      setQuery,
      sqCombo,
      subqs
    }: MenuComponentProps) => {
      const onClick = () => sqCombo.toggleDropdown();
      const subquestionItems = [{
        id: '_',
        title: 'Main question'
      }, ...(subqs?.map(s => ({
        id: s.id,
        title: s.cfg.title
      })) ?? [])];
      const commentSubquestionTitle = subquestionItems?.find(s => s.id == selectedSubq)?.title;
      return inPopup || selectedSubq && displayMode == 'flat' ? <Combo store={sqCombo} withinPortal position='bottom-end' keepMounted onOptionSubmit={v => {
        setQuery({
          subquestion: v
        });
        sqCombo.closeDropdown();
      }}>
            <Combo.Target>
              <div className='flex items-center shrink min-w-0'>
                <MentionQuestionBadge className='flex items-center min-w-0' onClick={onClick}>
                  <div className='truncate min-w-0'>
                    {selectedSubq ? commentSubquestionTitle : 'select subquestion'}
                  </div>
                  <div>
                    <HiSelector size={14} />
                  </div>
                </MentionQuestionBadge>
                {selectedSubq && <CloseButton size='sm' onMouseDown={event => event.preventDefault()} onClick={() => setQuery({
              subquestion: undefined
            })} />}
              </div>
            </Combo.Target>
            <Combo.Dropdown className='flex items-center !w-[min(30em,95vw)]'>
              <Combo.Options>
                {subquestionItems.map(({
              id,
              title
            }) => <Combo.Option key={id} value={id} className='flex items-center gap-2'>
                    {id == selectedSubq && <CheckIcon size={12} />}
                    {title}
                  </Combo.Option>)}
              </Combo.Options>
            </Combo.Dropdown>
          </Combo> : undefined;
    }
  }
} satisfies Record<string, {
  section: 'filter' | 'menu';
  El: (props: MenuComponentProps) => ReactNode;
}>;
const TopMenu = t0 => {
  const $ = _c(34);
  const {
    menuLeft,
    displayMode,
    setDisplayMode,
    subqs,
    query,
    setQuery
  } = t0;
  const searchStore = useSearchComments();
  const [menuOpen, setMenuOpen] = useState(false);
  const t1 = useCombobox();
  let t2;
  if ($[0] !== displayMode || $[1] !== query || $[2] !== searchStore || $[3] !== setDisplayMode || $[4] !== setQuery || $[5] !== subqs || $[6] !== t1) {
    t2 = {
      displayMode,
      setDisplayMode,
      query,
      setQuery,
      searchStore,
      setMenuOpen,
      subqs,
      sqCombo: t1
    };
    $[0] = displayMode;
    $[1] = query;
    $[2] = searchStore;
    $[3] = setDisplayMode;
    $[4] = setQuery;
    $[5] = subqs;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const menuProps = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      size: "18px"
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== menuLeft) {
    t4 = <div>{menuLeft}</div>;
    $[9] = menuLeft;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Menu.Target><ActionIcon><HiDotsVertical size="1.3em" /></ActionIcon></Menu.Target>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== menuProps) {
    t6 = entries(menuItems).filter(_temp21).map(t7 => {
      const [key, t8] = t7;
      const {
        El
      } = t8;
      return <Fragment key={key}><El {...menuProps} inPopup={true} /></Fragment>;
    });
    $[12] = menuProps;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Menu.Divider />;
    t8 = <Menu.Label>Filters</Menu.Label>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[16] !== menuProps) {
    t9 = entries(menuItems).filter(_temp22).map(t10 => {
      const [key_0, t11] = t10;
      const {
        El: El_0
      } = t11;
      return <Fragment key={key_0}><El_0 {...menuProps} inPopup={true} /></Fragment>;
    });
    $[16] = menuProps;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <div className="space-y-2 mx-2">{t9}</div>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t6) {
    t11 = <Menu.Dropdown className="max-w-[95vw] min-w-[20em]">{t6}{t7}{t8}{t10}</Menu.Dropdown>;
    $[20] = t10;
    $[21] = t6;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== menuOpen || $[24] !== t11) {
    t12 = <Menu opened={menuOpen} onChange={setMenuOpen}>{t5}{t11}</Menu>;
    $[23] = menuOpen;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== menuProps) {
    t13 = entries(menuItems).map(t14 => {
      const [key_1, t15] = t14;
      const {
        El: El_1
      } = t15;
      return <Fragment key={key_1}><El_1 {...menuProps} inPopup={false} /></Fragment>;
    });
    $[26] = menuProps;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = <div className="flex shrink min-w-0 items-center flex-row-reverse gap-1">{t12}{t13}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14 || $[32] !== t4) {
    t15 = <IconContext.Provider value={t3}><div className="grid grid-cols-[auto,1fr] pr-2 py-2 border-b">{t4}{t14}</div></IconContext.Provider>;
    $[31] = t14;
    $[32] = t4;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
};
const useSearchComments = () => {
  const $ = _c(14);
  const {
    question_id
  } = useQuestionContext();
  let t0;
  if ($[0] !== question_id) {
    t0 = {
      question_id
    };
    $[0] = question_id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data
  } = useCommentsQuery(t0);
  const {
    profilesUI
  } = usePortal();
  let t1;
  if ($[2] !== data || $[3] !== profilesUI) {
    t1 = data?.map(c => ({
      ...c,
      profile: profilesUI?.[c.created_by]
    }));
    $[2] = data;
    $[3] = profilesUI;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const comments = t1;
  const [search, setSearch] = useState("");
  const [searchDebounced] = useDebouncedValue(search, 50);
  const combo = useCombobox({
    onDropdownClose: () => {
      combo.resetSelectedOption();
      combo.focusTarget();
      setSearch("");
    },
    onDropdownOpen: () => {
      combo.focusSearchInput();
    }
  });
  let t2;
  if ($[5] !== comments) {
    t2 = new Fuse(comments ?? [], {
      keys: ["cfg.text"],
      includeMatches: true,
      threshold: 0.1,
      minMatchCharLength: 3,
      ignoreLocation: true,
      useExtendedSearch: true,
      getFn: _temp23
    });
    $[5] = comments;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const fuse = t2;
  let t3;
  if ($[7] !== fuse || $[8] !== searchDebounced) {
    t3 = fuse.search(searchDebounced);
    $[7] = fuse;
    $[8] = searchDebounced;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const results = t3;
  let t4;
  if ($[10] !== combo || $[11] !== results || $[12] !== search) {
    t4 = {
      combo,
      search,
      setSearch,
      results
    };
    $[10] = combo;
    $[11] = results;
    $[12] = search;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
type UseSearchComments = ReturnType<typeof useSearchComments>;
type SearchCommentProps = {
  store: UseSearchComments;
  onCommentSelected: (id: string) => void;
  target?: JSX.Element;
};
const SearchComments = t0 => {
  const $ = _c(26);
  const {
    store: t1,
    onCommentSelected,
    target
  } = t0;
  const {
    combo,
    search,
    setSearch,
    results
  } = t1;
  let t2;
  if ($[0] !== combo || $[1] !== onCommentSelected) {
    t2 = id => {
      combo.closeDropdown();
      onCommentSelected(id);
    };
    $[0] = combo;
    $[1] = onCommentSelected;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== combo || $[4] !== target) {
    t3 = target ?? <ActionIcon variant="subtle" size="md" onClick={() => combo.openDropdown()}><HiMagnifyingGlass /></ActionIcon>;
    $[3] = combo;
    $[4] = target;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <Combo.Target>{t3}</Combo.Target>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== setSearch) {
    t5 = event => setSearch(event.currentTarget.value);
    $[8] = setSearch;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== search || $[11] !== t5) {
    t6 = <Combo.Search size="md" value={search} onChange={t5} placeholder="Search comments" />;
    $[10] = search;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== results || $[14] !== search) {
    t7 = results.length > 0 ? results?.map(_temp25) : search ? <Combo.Empty>Nothing found</Combo.Empty> : <Combo.Empty>Search to see results</Combo.Empty>;
    $[13] = results;
    $[14] = search;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <div className="max-h-[80vh] overflow-y-auto"><Combo.Options>{t7}</Combo.Options></div>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t8) {
    t9 = <Combo.Dropdown className="overflow-y-auto !p-1">{t6}{t8}</Combo.Dropdown>;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== combo || $[22] !== t2 || $[23] !== t4 || $[24] !== t9) {
    t10 = <Combo store={combo} withinPortal={true} position="bottom-end" width="70em" onOptionSubmit={t2}>{t4}{t9}</Combo>;
    $[21] = combo;
    $[22] = t2;
    $[23] = t4;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
export const CommentsStats = t0 => {
  const $ = _c(6);
  const {
    variant,
    unread,
    total,
    tipFooter,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== tipFooter || $[2] !== total || $[3] !== unread || $[4] !== variant) {
    t1 = unread != null && total != null ? <Tooltip label={<span><b className="text-primary">{unread}</b> / <b>{total}</b> new comment{total == 1 ? "" : "s"} since you last saw the question. <br />{tipFooter}</span>}><div className={className}><WrapIf if={unread > 0 && unread != total} wrap={c => <Indicator inline={true} label={<b className="text-xs">{unread}</b>} color="primary" position="bottom-end" size={14} offset={match(variant).with("subtle", _temp26).otherwise(_temp27)} disabled={!unread}>{c}</Indicator>}><div className="relative">{match(variant).with("subtle", () => <div className={`text-sm text-gray-400 ${unread ? "pb-1" : ""}`}>{total}</div>).otherwise(() => <><HiChatBubbleLeft size={24} color={`var(--mantine-${!total ? "color-gray-3" : total == unread ? "primary-color-filled" : "color-gray-5"}`} /><span className="absolute top-[3px] w-full text-center text-xs font-semibold text-white">{total}</span></>)}</div></WrapIf></div></Tooltip> : <></>;
    $[0] = className;
    $[1] = tipFooter;
    $[2] = total;
    $[3] = unread;
    $[4] = variant;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export const CommentsComponent = t0 => {
  const $ = _c(5);
  const {
    comments,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== comments) {
    let t2;
    if ($[3] !== className) {
      t2 = cs => <div className={`${className}`}>{mapWithPrev(cs, _temp31)}</div>;
      $[3] = className;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = match(comments).with(P.nullish, _temp29).with([], _temp30).otherwise(t2);
    $[0] = className;
    $[1] = comments;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp21(t0) {
  const [, t1] = t0;
  const {
    section
  } = t1;
  return section == "menu";
}
function _temp22(t0) {
  const [, t1] = t0;
  const {
    section: section_0
  } = t1;
  return section_0 == "filter";
}
function _temp23(obj, pathRaw) {
  const path = (isArray(pathRaw) ? pathRaw.join(".") : pathRaw) as Paths<CommentRow>;
  const val = pathValue(obj, path) as string;
  return match(path).with("cfg.text", () => Md.maskNonText(val)).otherwise(() => val);
}
function _temp24(m) {
  return m.key == "cfg.text";
}
function _temp25(t0) {
  const {
    item: t1,
    matches
  } = t0;
  const {
    id: id_0,
    cfg,
    profile,
    created_at
  } = t1;
  return <Combo.Option key={id_0} value={id_0} className="data-[combobox-selected=true]:!bg-primary-500/10 data-[combobox-selected=true]:!text-gray-900 "><div className="flex gap-1"><SAvatar profile={profile} size="sm" /><div className="pointer-events-none"><Markdown rehypePlugins={[rehypeRaw]}>{markMd({
            md: cfg.text,
            marks: matches?.find(_temp24)?.indices
          })}</Markdown><div className="whitespace-nowrap text-sm text-gray-500">{fmtDate(created_at, {
            showTime: true
          })}</div></div></div></Combo.Option>;
}
function _temp26() {
  return 2;
}
function _temp27() {
  return 6;
}
function _temp28() {
  return <CommentSkeleton />;
}
function _temp29() {
  return new Array(3).map(_temp28);
}
function _temp30() {
  return <></>;
}
function _temp31(c, t0) {
  const {
    prev,
    next
  } = t0;
  return <SErrorBoundary key={c.comment.id}><Comment {...{
      ...c,
      prev: prev?.comment,
      next: next?.comment
    }} /></SErrorBoundary>;
}